// AggregatorApplications.jsx - Place in /workspaces/mcn-youtube-platform/mcn-frontend/src/components/dashboards/AggregatorDashboard/AggregatorApplications.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL as API_URL } from '../../../config';
import './AggregatorApplications.css';

const AggregatorApplications = () => {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    rejectedApplications: 0,
    totalCommission: 0,
    pendingCommission: 0
  });
  const [loading, setLoading] = useState(true);
  const [referralLink, setReferralLink] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    fetchApplications();
    generateReferralLink();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/aggregator/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setApplications(response.data.applications || []);
      calculateStats(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (apps) => {
    const stats = {
      totalApplications: apps.length,
      pendingApplications: apps.filter(a => a.status === 'PENDING_REVIEW').length,
      approvedApplications: apps.filter(a => a.status === 'APPROVED').length,
      rejectedApplications: apps.filter(a => a.status === 'REJECTED').length,
      totalCommission: apps.filter(a => a.status === 'APPROVED').length * 500, // ₹500 per approved
      pendingCommission: apps.filter(a => a.status === 'PENDING_REVIEW').length * 500
    };
    setStats(stats);
  };

  const generateReferralLink = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const baseUrl = window.location.origin;
    const referralCode = user.referralCode || `AGG_${user.id}`;
    setReferralLink(`${baseUrl}/apply?ref=${referralCode}`);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING_REVIEW': 'badge-warning',
      'UNDER_REVIEW': 'badge-info',
      'APPROVED': 'badge-success',
      'REJECTED': 'badge-danger',
      'ON_HOLD': 'badge-secondary'
    };
    return badges[status] || 'badge-default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading">Loading applications...</div>;
  }

  return (
    <div className="aggregator-applications">
      {/* Referral Section */}
      <div className="referral-section">
        <h3>Your Referral Link</h3>
        <div className="referral-link-container">
          <input
            type="text"
            value={referralLink}
            readOnly
            className="referral-input"
          />
          <button onClick={copyToClipboard} className="copy-btn">
            {copySuccess ? '✓ Copied!' : 'Copy Link'}
          </button>
        </div>
        <p className="referral-info">
          Share this link with potential creators. You'll earn ₹500 for each approved application.
        </p>
      </div>

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-details">
            <div className="stat-value">{stats.totalApplications}</div>
            <div className="stat-label">Total Applications</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-details">
            <div className="stat-value">{stats.pendingApplications}</div>
            <div className="stat-label">Pending Review</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-details">
            <div className="stat-value">{stats.approvedApplications}</div>
            <div className="stat-label">Approved</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-details">
            <div className="stat-value">₹{stats.totalCommission}</div>
            <div className="stat-label">Earned Commission</div>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="applications-section">
        <h3>Referred Applications</h3>
        <div className="applications-table">
          <table>
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Channel</th>
                <th>Niche</th>
                <th>Subscribers</th>
                <th>Status</th>
                <th>Applied On</th>
                <th>Commission</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(app => (
                <tr key={app.id}>
                  <td>
                    <div className="applicant-info">
                      <div className="name">{app.firstName} {app.lastName}</div>
                      <div className="email">{app.email}</div>
                    </div>
                  </td>
                  <td>
                    <div className="channel-name">{app.channelName}</div>
                  </td>
                  <td>{app.contentNiche}</td>
                  <td>{app.currentSubscribers?.toLocaleString() || '0'}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(app.status)}`}>
                      {app.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{formatDate(app.submittedAt)}</td>
                  <td>
                    <span className={`commission ${app.status === 'APPROVED' ? 'earned' : 'pending'}`}>
                      {app.status === 'APPROVED' ? '₹500' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {applications.length === 0 && (
            <div className="no-data">
              <div className="no-data-icon">👥</div>
              <h4>No applications yet</h4>
              <p>Share your referral link to start earning commissions!</p>
            </div>
          )}
        </div>
      </div>

      {/* Performance Tips */}
      <div className="tips-section">
        <h3>Tips to Increase Referrals</h3>
        <div className="tips-grid">
          <div className="tip-card">
            <div className="tip-icon">🎯</div>
            <h4>Target Right Creators</h4>
            <p>Focus on creators with 1000+ subscribers who are actively posting content.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">📱</div>
            <h4>Use Social Media</h4>
            <p>Share your link on creator communities, Facebook groups, and WhatsApp.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">🤝</div>
            <h4>Build Relationships</h4>
            <p>Connect with creators personally and explain the benefits of joining.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">📊</div>
            <h4>Track Performance</h4>
            <p>Monitor which channels bring the best creators and focus on those.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AggregatorApplications;